import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
} from "@mui/material";
import axios from "../utils/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const styles = {
  pageWrap: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: 800,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      bgcolor: "#f5f5f5",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
    mt: 0.5,
  },
  formCard: {
    bgcolor: "white",
    borderRadius: 3,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.06)",
    p: { xs: 2, sm: 3 },
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#fafbfc",
      "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
      "&:hover fieldset": { borderColor: "rgba(0,0,0,0.2)" },
      "&.Mui-focused fieldset": {
        borderColor: "#0d47a1",
        borderWidth: "2px",
      },
    },
  },
  datePickerWrap: {
    "& .react-datepicker-wrapper": { width: "100%" },
    "& .react-datepicker__input-container input": {
      width: "100%",
      padding: "16.5px 14px",
      fontSize: "1rem",
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.23)",
      backgroundColor: "#fafbfc",
      fontFamily: "inherit",
      boxSizing: "border-box",
    },
    "& .react-datepicker__input-container input:focus": {
      outline: "none",
      borderColor: "#0d47a1",
      borderWidth: "2px",
      padding: "15.5px 13px",
    },
  },
  submitButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "1rem",
    borderRadius: 2,
    py: 1.5,
    px: 3,
    background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
    boxShadow: "0 2px 8px rgba(13, 71, 161, 0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #0a3d91 0%, #0d47a1 100%)",
      boxShadow: "0 4px 12px rgba(13, 71, 161, 0.4)",
    },
  },
};

export default function BookSession() {
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [address, setAddress] = useState("");
  const [bookedDate, setBookedDate] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [customerBlockedDates, setCustomerBlockedDates] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchBlockedDates = async () => {
      try {
        const response = await axios.get("/blocked-dates");
        const blocked = response.data.blockedDates.map((date) => new Date(date));
        const customerBlocked = response.data.customerBlockedDates.map(
          (date) => new Date(date)
        );
        setBlockedDates(blocked);
        setCustomerBlockedDates(customerBlocked);
      } catch (err) {
        setError("Error fetching blocked dates. Please try again.");
        console.error("Error fetching blocked dates", err);
      }
    };
    fetchBlockedDates();
  }, []);

  const handleDateChange = (date) => {
    setBookedDate(date);
  };

  const validateForm = () => {
    if (!name || !companyName || !email || !mobile || !address || !bookedDate) {
      return "All fields are required.";
    }
    const emailPattern = /\S+@\S+\.\S+/;
    if (!emailPattern.test(email)) {
      return "Please enter a valid email address.";
    }
    const mobilePattern = /^\d{10}$/;
    if (!mobilePattern.test(mobile)) {
      return "Please enter a valid 10-digit mobile number.";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    const formattedDate = bookedDate
      ? moment(bookedDate).format("YYYY-MM-DD")
      : "";

    try {
      await axios.post("/book-session", {
        name,
        companyName,
        email,
        mobileNumber: mobile,
        address,
        bookedDate: formattedDate,
      });
      setSuccess("Booking submitted successfully. Redirecting…");
      setError("");
      resetForm();
      setTimeout(() => navigate("/Booking-details"), 1000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Error booking session. Please try again."
      );
      setSuccess("");
      console.error("Booking failed:", err.response?.data?.message || err);
    }
  };

  const resetForm = () => {
    setName("");
    setCompanyName("");
    setEmail("");
    setMobile("");
    setAddress("");
    setBookedDate(null);
  };

  const isSunday = (date) => date.getDay() === 0;

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>Book a session</Typography>
            <Typography sx={styles.subtitle}>
              Schedule a robot demo or session for your organization
            </Typography>
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={styles.formCard}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                required
                sx={styles.input}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter your company name"
                required
                sx={styles.input}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                sx={styles.input}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit mobile number"
                required
                inputProps={{ maxLength: 10 }}
                sx={styles.input}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter your address"
                required
                sx={styles.input}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={styles.datePickerWrap}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "rgba(0,0,0,0.6)",
                    marginBottom: 8,
                  }}
                >
                  Select date
                </label>
                <DatePicker
                  selected={bookedDate}
                  onChange={handleDateChange}
                  minDate={new Date()}
                  excludeDates={[...blockedDates, ...customerBlockedDates]}
                  placeholderText="Choose a date"
                  filterDate={(date) => !isSunday(date)}
                  renderDayContents={(day, date) => (
                    <span style={{ color: isSunday(date) ? "#b91c1c" : "inherit" }}>
                      {day}
                    </span>
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={styles.submitButton}
                >
                  Submit booking
                </Button>
              </Box>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setError("")}>
                  {error}
                </Alert>
              </Grid>
            )}
            {success && (
              <Grid item xs={12}>
                <Alert severity="success">{success}</Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
